import { IPricingRecord } from "@services/QuoteBuilder/QuoteBuilderService";
import { PlanType } from "@utils/quotePlanOptions";

export enum MasterSheetFilter {
  COUNTRY =  "country",
  STATE = "state",
  PLAN = "plan",
  VEHICLE_CONDITION = "vehicleCondition",
  CONTRACT_TERM = "contractTerm",
  VEHICLE_MAKE = "vehicleMake",
  VEHICLE_MODEL = "vehicleModel",
  VEHICLE_MODEL_YEAR = "vehicleModelYear",
  DISTANCE_ALLOWANCE = 'distanceAllowance',
  INITIAL_CONTRIBUTION = 'initialContribution',
  BALLOON_PAYMENT = 'balloonPayment'
}

export type SelectedVehicleState = {
  selectedCountry: string;
  selectedState: string;
  selectedPlan: string;
  selectedVehicleCondition: string;
  selectedContractTerm: string;
  selectedVehicleMake: string;
  selectedVehicleModel: string;
  selectedVehicleModelYear: string;
  selectedDistanceAllowance: string;
  selectedInitialContribution: string;
  selectedBalloonPayment: string;
  selectedPricingRecord?: string;
  validFilters: MasterSheetFilter[];
} | null;

/**
 * Creates initial filter state from pricing record for new quotes
 * @param pricingRecord - The pricing record from the API
 * @returns Initial filter state
 */
export function createInitialFilterState(
  pricingRecord: IPricingRecord
): SelectedVehicleState {
  if (pricingRecord.plan === PlanType.FLEXI_TRY) {
    return {
      selectedCountry: pricingRecord.country,
      selectedState: pricingRecord.state,
      selectedPlan: pricingRecord.plan,
      selectedVehicleCondition: pricingRecord.vehicle_condition,
      selectedContractTerm: pricingRecord.term_length,
      selectedVehicleMake: "",
      selectedVehicleModel: "",
      selectedVehicleModelYear: pricingRecord.model_year,
      selectedPricingRecord: "",
      selectedDistanceAllowance: pricingRecord.weekly_distance_allowance_id,
      selectedInitialContribution: pricingRecord.initial_contribution_id || '0',
      selectedBalloonPayment: pricingRecord.balloon_payment_id || '0',
      validFilters: [MasterSheetFilter.COUNTRY, MasterSheetFilter.STATE, MasterSheetFilter.PLAN,
        MasterSheetFilter.VEHICLE_MAKE, MasterSheetFilter.VEHICLE_MODEL],
    }
  }
  else {
    return {
      selectedCountry: pricingRecord.country,
      selectedState: pricingRecord.state,
      selectedPlan: pricingRecord.plan,
      selectedVehicleCondition: pricingRecord.vehicle_condition,
      selectedContractTerm: "",
      selectedVehicleMake: "",
      selectedVehicleModel: "",
      selectedVehicleModelYear: "",
      selectedDistanceAllowance: "",
      selectedInitialContribution: "",
      selectedBalloonPayment: "",
      selectedPricingRecord: "",
      validFilters: [
        MasterSheetFilter.COUNTRY, MasterSheetFilter.STATE, MasterSheetFilter.PLAN,
        MasterSheetFilter.VEHICLE_CONDITION, MasterSheetFilter.CONTRACT_TERM, MasterSheetFilter.VEHICLE_MAKE,
        MasterSheetFilter.VEHICLE_MODEL, MasterSheetFilter.VEHICLE_MODEL_YEAR, MasterSheetFilter.DISTANCE_ALLOWANCE,
        MasterSheetFilter.INITIAL_CONTRIBUTION, MasterSheetFilter.BALLOON_PAYMENT,
      ],
  };
}
}

export type SelectedVehicleAction =
  | {
      type: "USER_SELECTS_CONTRACT_TERM";
      payload: {
        selectedContractTerm: string;
      };
    }
  | {
      type: "USER_SELECTS_VEHICLE_MAKE";
      payload: {
        selectedVehicleMake: string;
      };
    }
  | {
      type: "USER_SELECTS_VEHICLE_MODEL";
      payload: {
        selectedVehicleModel: string;
      };
    }
  | {
      type: "USER_SELECTS_VEHICLE_MODEL_YEAR";
      payload: {
        selectedVehicleModelYear: string;
      };
    }
  | {
    type: "USER_SELECTS_DISTANCE_ALLOWANCE";
    payload: {
      selectedDistanceAllowance: string;
    };
  }
  | {
    type: "USER_SELECTS_INITIAL_CONTRIBUTION";
    payload: {
      selectedInitialContribution: string;
    };
  }
  | {
    type: "USER_SELECTS_BALLOON_PAYMENT";
    payload: {
      selectedBalloonPayment: string;
    };
  }
  | {
      type: "SET_SELECTED_COUNTRY";
      payload: {
        selectedCountry: string;
      };
    }
  | {
      type: "SET_SELECTED_STATE";
      payload: {
        selectedState: string;
      };
    }
  | {
      type: "SET_SELECTED_PLAN";
      payload: {
        selectedPlan: string;
      };
    }
  | {
      type: "SET_SELECTED_VEHICLE_CONDITION";
      payload: {
        selectedVehicleCondition: string;
      };
    }
  | {
      type: "SET_FILTER";
      payload: SelectedVehicleState;
    };

export function selectedVehicleReducer(
  state: SelectedVehicleState,
  action: SelectedVehicleAction
): SelectedVehicleState {
  switch (action.type) {
    case "USER_SELECTS_CONTRACT_TERM":
      console.log(
        `User selected contract term: ${action.payload.selectedContractTerm}`
      );
      if (state) {
        return {
          ...state,
          selectedContractTerm: action.payload.selectedContractTerm,
        };
      }
      return state;
    case "USER_SELECTS_VEHICLE_MAKE":
      console.log(
        `User selected vehicle make: ${action.payload.selectedVehicleMake}`
      );
      if (state) {
        return {
          ...state,
          selectedVehicleMake: action.payload.selectedVehicleMake,
        };
      }
      return state;
    case "USER_SELECTS_VEHICLE_MODEL":
      console.log(
        `User selected vehicle model: ${action.payload.selectedVehicleModel}`
      );
      if (state) {
        return {
          ...state,
          selectedVehicleModel: action.payload.selectedVehicleModel,
        };
      }
      return state;
    case "USER_SELECTS_VEHICLE_MODEL_YEAR":
      console.log(
        `User selected vehicle model year: ${action.payload.selectedVehicleModelYear}`
      );
      if (state) {
        return {
          ...state,
          selectedVehicleModelYear: action.payload.selectedVehicleModelYear,
        };
      }
      return state;
    case "USER_SELECTS_DISTANCE_ALLOWANCE":
        console.log(`User selected distance allowance: ${action.payload.selectedDistanceAllowance}`);
        if (state) {
          return {
            ...state,
            selectedDistanceAllowance: action.payload.selectedDistanceAllowance
          };
        }
        return state;
    case "USER_SELECTS_INITIAL_CONTRIBUTION":
        console.log(`User selected initial contribution: ${action.payload.selectedInitialContribution}`);
        if (state) {
          return {
            ...state,
            selectedInitialContribution: action.payload.selectedInitialContribution
          };
        }
        return state;
    case "USER_SELECTS_BALLOON_PAYMENT":
        console.log(`User selected balloon payment: ${action.payload.selectedBalloonPayment}`);
        if (state) {
          return {
            ...state,
            selectedBalloonPayment: action.payload.selectedBalloonPayment
          };
        }
        return state;
    case "SET_SELECTED_COUNTRY":
      console.log(`Selected Country: ${action.payload.selectedCountry}`);
      if (state) {
        return {
          ...state,
          selectedCountry: action.payload.selectedCountry,
        };
      }
      return state;
    case "SET_SELECTED_PLAN":
      console.log(`Selected Plan: ${action.payload.selectedPlan}`);
      if (state) {
        return {
          ...state,
          selectedPlan: action.payload.selectedPlan,
        };
      }
      return state;
    case "SET_SELECTED_STATE":
      console.log(`Selected State: ${action.payload.selectedState}`);
      if (state) {
        return {
          ...state,
          selectedState: action.payload.selectedState,
        };
      }
      return state;
    case "SET_SELECTED_VEHICLE_CONDITION":
      console.log(
        `Selected Vehicle Condition: ${action.payload.selectedVehicleCondition}`
      );
      if (state) {
        return {
          ...state,
          selectedVehicleCondition: action.payload.selectedVehicleCondition,
        };
      }
      return state;
    case "SET_FILTER":
      return action.payload;
    default:
      console.log(`Error - incorrect action sent ${action} ${state}`);
      return state;
  }
}
